import { useEffect, useCallback, useState, useContext } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

export const useBlocker = (blocker, when = true) => {
  const { navigator } = useContext(NavigationContext);

  useEffect(() => {
    if (!when) return;

    const push = navigator.push;
    const replace = navigator.replace;

    navigator.push = (...args) => {
      const result = blocker(() => push(...args));
      if (result !== false) {
        push(...args);
      }
    };

    navigator.replace = (...args) => {
      const result = blocker(() => replace(...args));
      if (result !== false) {
        replace(...args);
      }
    };

    return () => {
      navigator.push = push;
      navigator.replace = replace;
    };
  }, [navigator, blocker, when]);
};

export const useChangeDetection = (originalData, currentData, isOpen) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [retry, setRetry] = useState(null);

  const hasChanges = useCallback(() => {
    if (!originalData || !currentData) return false;

    return Object.keys(currentData).some((key) => {
      const currentValue = currentData[key];
      const originalValue = originalData[key];

      if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
        if (currentValue.length !== originalValue.length) return true;
        return !currentValue.every((item, index) => {
          const originalItem = originalValue[index];
          if (typeof item === 'object' && typeof originalItem === 'object') {
            return JSON.stringify(item) === JSON.stringify(originalItem);
          }
          return item === originalItem;
        });
      }

      if (
        typeof currentValue === 'object' &&
        typeof originalValue === 'object'
      ) {
        return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
      }

      return currentValue !== originalValue;
    });
  }, [originalData, currentData]);

  useBlocker(
    useCallback(
      (retryFn) => {
        if (isOpen && hasChanges()) {
          setShowPrompt(true);
          setRetry(() => retryFn);
          return false;
        }
        return true;
      },
      [isOpen, hasChanges]
    ),
    isOpen
  );

  const confirmLeave = useCallback(() => {
    if (retry) {
      retry();
      setRetry(null);
    }
    setShowPrompt(false);
  }, [retry]);

  const cancelLeave = useCallback(() => {
    setRetry(null);
    setShowPrompt(false);
  }, []);

  return {
    showPrompt,
    setShowPrompt,
    confirmLeave,
    cancelLeave,
    hasChanges: hasChanges,
  };
};

export default useChangeDetection;
